/**
 * 🎲 **영상 seed 관문 SSOT**(익스퍼트 이식 PR-1b · 적대 1R P2) — "이 값을 제출·기록·승계해도 되는가".
 *
 * ══ 왜 모듈로 뽑았나 ═══════════════════════════════════════════════════════════════════════
 * 같은 좁힘이 ★네 곳★에 손으로 복제돼 있었다(요청 바디 조각 · 굽기 스탬프 기록 · 재생성 승계 ·
 * 캡슐 sanitize). 네 벌은 갈린다 — 한 곳만 고치면 "승계했다고 믿는데 다음 층이 떨어뜨리는" 침묵
 * 실패가 되고, 그 실패는 로그도 배지도 남기지 않는다(무변경 재생성이 그냥 계속 흔들린다).
 *
 * ══ 무엇을 통과시키나(정본 §15.2 `[공식-API]` 대조 2026-08-20) ═════════════════════════════
 * · 벤더 표는 `seed | integer | 2.5 지원 (기본 -1)` 까지만 말한다 — ★상·하한을 문서화하지 않았다★.
 * · ★음수는 거른다★: 벤더 기본값 `-1` 이 곧 "무작위로 뽑아라"라, 그 값을 ★승계★ 하면 "고정했는데
 *   매번 달라지는" 자기모순이 된다(이 축의 존재 이유가 사라진다). 캡슐 관문(`scene-gen-spec` 의
 *   `c.seed >= 0`)이 이미 같은 선을 그어 두었다.
 * · ★상한은 보수적으로 2^31-1★ — 문서가 범위를 안 주므로 32bit 부호 있는 정수의 최대값을 쓴다.
 *   벤더가 더 큰 값을 받더라도 우리가 못 싣는 것은 ★잃는 것이 없고★(seed 는 우리가 발급하지 않는다),
 *   반대로 벤더 한계를 넘겨 보내면 비동기 400 으로 그 컷이 통째로 죽는다. 문서에 범위가 명시되면
 *   이 상수 하나만 고친다.
 * ★EF 관문(`generate-sedeo-video/submit-policy.resolveSedeoSeed`)보다 좁다 — 의도된 비대칭이다★:
 *   EF 는 "요청에 실어도 되는가"(정수·유한)를 보고, 이 모듈은 "★승계·기록할 값인가★"를 본다.
 *   `-1`(무작위 지시)은 전자를 통과하지만 후자에는 해당하지 않는다.
 */

// 🎬 seed 를 존중하는 레인 판정의 재료 — 모델 어휘 SSOT(EF `_shared` 사본과 쌍으로 잠긴 상수).
import { SEDEO_25_MODEL_ID } from "./sedeo-models";

/**
 * 🎲 승계·제출 가능한 seed 의 상한 — ★벤더 문서 근거가 아니라 보수적 방어선★(위 모듈 주석).
 *   정본 §15.2 가 범위를 명시하면 이 값을 그 근거로 교체한다(그때 주석도 함께 고친다).
 */
export const VIDEO_SEED_MAX = 2147483647;

/**
 * 🎲 이 값이 ★그대로 요청에 실어도·스탬프에 적어도·다음 회차에 승계해도 되는 seed★ 인가.
 *
 * ★부재와 0 을 반드시 가른다★ — 0 은 벤더가 받는 정당한 seed 라, falsy 로 접으면 "seed 0 으로
 *   고정한 컷"만 매번 새 난수를 타는 조용한 예외가 생긴다.
 * ★타입 술어(`v is number`)로 낸다★ — 호출부가 관문을 통과한 값을 다시 좁히거나 캐스트하지 않게.
 */
export function isSubmittableSeed(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= VIDEO_SEED_MAX;
}

/**
 * 🎲 **이 모델이 seed 를 ★실제로 존중하는가★**(적대 2R P2) — "실어 보냈다"와 "그 값으로 뽑힌다"의 경계.
 *
 * ══ EF 대응 지점 ═══════════════════════════════════════════════════════════════════════════
 * `supabase/functions/generate-sedeo-video/submit-policy.ts` 의 `resolveSedeoSeed(raw, sedeo25Model)`
 * 첫 줄 — `if (!sedeo25Model) return null`. 즉 **드롭 판정의 정본은 EF 다**. 이 함수는 그 판정을
 * 복제해 요청을 바꾸려는 것이 ★아니다★(프론트는 모델과 무관하게 실어 보내고, 드롭은 EF 소관 —
 * 그 층 소유 계약은 `cinematicCanvasGeneration.sedeoSeedPatch` 주석이 정본이다).
 * ★소비처는 ★둘★ 이다(적대 5R 정정 — 종전 "고지 하나뿐" 서술은 틀렸다)★:
 *   ①**고지** — "설정이 그대로라 거의 같은 영상이 나와요"는 seed 가 존중되는 레인에서만 참이다.
 *     2.0 회차에 그 말을 하면 거짓 고지이고, 유저는 "그대로라며 왜 달라졌냐"를 겪는다.
 *   ②**발급 게이트**({@link mintSeedForLane}) — 그래서 이 술어가 갈리면 ★양방향으로 대가가 있다★:
 *     · ★좁으면(여기가 뒤처지면) 기능이 침묵한다★ — 실제로는 존중되는 모델인데 발급을 안 해
 *       무변경 재생성이 계속 흔들린다(BUG-22 미해소 · 아무 로그도 안 남는다).
 *     · ★넓으면(여기가 앞서면) 거짓 기록이다★ — EF 가 떨군 값을 "이걸로 다시 뽑으면 같다"로 남긴다.
 *   ⇒ 두 벌이 갈리지 않게 ★모델 어휘를 EF `_shared` 사본과 값으로 대조★ 한다(교차 계약 테스트 ·
 *     `tests/unit/expert-pr1b-seed.test.ts`). 지원 모델이 늘면 두 파일을 ★함께★ 고쳐야 한다.
 */
export function sedeoSeedIsHonored(model: string | null | undefined): boolean {
  return (model ?? "").trim() === SEDEO_25_MODEL_ID;
}

/**
 * 🎲 **seed 발급 — ★내부 유일 지점★**(적대 3R P2 치명 · 2026-08-20).
 *
 * ══ 왜 필요했나(없으면 이 레인 전체가 죽은 배선이다) ═══════════════════════════════════════
 * 관통·기록·승계를 다 배선하고도 ★원천이 0★ 이었다: 캡슐 `consistency.seed` 를 쓰는 화면이 없고
 * (UI 금지 계약), `videoStamp.seed` 를 찍는 호출부도 아직 없다 ⇒ `resolveRegenSeed` 가 언제나
 * `undefined` ⇒ 요청에 seed 가 안 실리고 BUG-22 는 그대로, 무변경 고지도 영원히 침묵.
 * ⇒ ★첫 회차에 한 번 뽑아 두는 자리★ 가 있어야 사슬이 처음 돈다. 그 자리가 여기 하나다.
 *
 * ★"유저 UI 금지"와 충돌하지 않는다★: 금지된 것은 ★유저가 값을 고르는 창구★ 이지, 시스템이 재현
 *   가능한 기준점을 잡는 것이 아니다. 유저에게는 숫자도 버튼도 보이지 않고, 화면이 하는 약속도 없다
 *   (그 약속은 `sameSettingRegenNotice` 가 자연어로만, 조건이 전부 참일 때만 한다).
 * ★EF 계약과도 무충돌★: EF(`submit-policy.resolveSedeoSeed`)는 "정수면 싣는다"이고 2.0 은 스스로
 *   떨군다. 발급은 ★호출부가 2.5 레인일 때만★ 한다(`sedeoSeedIsHonored`) — 존중되지 않는 레인에서
 *   뽑아 기록하면 "이 값으로 다시 뽑으면 같다"는 거짓 기록이 남는다.
 * ★암호학적 난수를 쓴다★ — `Math.random` 은 같은 틱에 여러 컷이 같은 값을 뽑을 수 있고(일관성 축이
 *   서로 다른 컷을 묶어 버린다), 이 레포는 그 함수를 seed 축에 못 쓰게 가드로 막아 두었다.
 * ★범위는 관문과 같다★(`isSubmittableSeed`) — 발급값이 자기 관문을 통과하지 못하는 일은 없어야 한다.
 */
export function mintVideoSeed(): number | undefined {
  /* 🛡 ★발급 실패가 유료 기능을 죽이지 않는다★(적대 5R P3) — `crypto` 는 비보안 컨텍스트·구형
     웹뷰·일부 임베드에서 없을 수 있다. 여기서 던지면 그 환경의 ★씬 재생성 전체★ 가 실패한다(원래
     결함은 "일관성 축이 없다"뿐인데 기능을 통째로 잃는다 = 막다른 길 금지 헌법 위반).
     ⇒ 그 회차만 seed 없이 간다(=종전 동작 · 벤더가 새로 뽑는다). 사실은 로그로 남긴다. */
  try {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    // 2^32 는 (VIDEO_SEED_MAX + 1)=2^31 의 배수라 나머지 연산이 편향을 만들지 않는다(정확히 균등).
    return buf[0] % (VIDEO_SEED_MAX + 1);
  } catch (e) {
    console.warn("[video-seed] 난수원이 없어 이 회차는 seed 없이 만들어요(결과가 매번 조금 달라질 수 있어요).", e);
    return undefined;
  }
}

/**
 * 🎲 **이 회차에 발급할 것인가 — 레인·모델 확정 게이트**(적대 5R P2).
 *
 * ★모델이 ★미확정★ 이면 발급을 보류하고 ★말한다★★: `readNodeVideoModel()` 은 저장분 파싱 실패·
 *   미설정에서 `undefined` 를 낼 수 있다. 그 값을 그대로 `sedeoSeedIsHonored` 에 넣으면 `false` 가 되어
 *   ★2.5 작업물인데도 조용히 발급이 안 되는★ 침묵 실패가 된다(고지도 안 뜨고 로그도 없다 —
 *   "왜 매번 다르지"를 아무도 못 짚는다). 미확정과 "2.0 이라서 안 함"은 ★다른 사실★ 이라 갈라 적는다.
 * ★반환은 값 하나다★ — 호출부가 조건을 다시 쓰지 않게(그 복제가 이 레인의 반복 결함이었다).
 */
export function mintSeedForLane(model: string | null | undefined): number | undefined {
  if (!(model ?? "").trim()) {
    console.warn("[video-seed] 영상 품질(모델)이 확정되지 않아 이번 회차는 seed 발급을 보류했어요 — 결과가 매번 조금 달라질 수 있어요.");
    return undefined;
  }
  return sedeoSeedIsHonored(model) ? mintVideoSeed() : undefined;
}
